import { LayoutMoon, TopNav, TopNavItem } from '@orobix/orobix-fe-components'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory, useLocation } from 'react-router-dom'

import manualEn from '../assets/orca-end-user-manual-en.pdf'
import manualIt from '../assets/orca-end-user-manual-it.pdf'
import { retrieveLoggedUser, useAuth, User } from '../auth/AuthProvider'
import { AppInfo } from '../components/AppInfo/AppInfo'
import { AppLogo } from '../components/AppLogo/AppLogo'
import { EnvInfo } from '../components/EnvInfo/EnvInfo'
import { vars } from '../utils/appConfig'
import { getEnvs } from '../utils/env'
import { locations } from '../utils/locations'

interface LoggedInTemplateProps {
  children: React.ReactNode
}

const { showEnvBadge } = getEnvs()

export const LoggedInTemplate = ({
  children,
}: LoggedInTemplateProps): JSX.Element => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { logout } = useAuth()
  const location = useLocation()
  const { bypassLogin } = getEnvs()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    retrieveLoggedUser()
      .then((loggedUser: User | null) => {
        setUser(loggedUser)
        setIsLoading(false)
      })
      .catch((error) => {
        setUser(null)
        setIsLoading(false)
      })
  }, [bypassLogin])

  const handleMenuClick = useCallback(
    (where: string) => history.push(where),
    [history]
  )

  const handleDownloadManual = useCallback(() => {
    const pdfPath = i18n.language === 'it' ? manualIt : manualEn
    const link = document.createElement('a')
    link.href = pdfPath
    link.download = `orca-end-user-manual-${
      i18n.language === 'it' ? 'it' : 'en'
    }.pdf`
    link.click()
  }, [i18n.language])

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!user && !bypassLogin) {
    return <Redirect to="/login" />
  }

  let userFullName = 'Guest'
  let userInitials = 'G'
  let userEmail = ''
  if (user) {
    userEmail = user.email
    // Handle both camelCase and snake_case field names
    const firstName = user.firstName || user.first_name || ''
    const lastName = user.lastName || user.last_name || ''
    userFullName = `${firstName} ${lastName}`.trim() || user.username || 'Guest'

    // Determine user initials
    if (firstName) {
      userInitials = firstName.charAt(0).toUpperCase()
    } else if (user.username) {
      userInitials = user.username.charAt(0).toUpperCase()
    } else {
      userInitials = 'G'
    }
  }

  return (
    <LayoutMoon
      appName={vars.appName}
      logo={<AppLogo />}
      subTitle="Operational Resource & Cost Analyzer"
      info={<AppInfo />}
      envBadge={showEnvBadge && <EnvInfo />}
      showUser
      userFullName={userFullName}
      userEmail={userEmail}
      userInitials={userInitials}
      userMenuItems={
        !bypassLogin
          ? [
              {
                type: 'button',
                label: t('Settings'),
                onClick: () => handleMenuClick(locations.settings),
              },
              {
                type: 'button',
                label: t('Download user manual'),
                onClick: handleDownloadManual,
              },
              {
                type: 'button',
                label: t('Logout'),
                onClick: () => logout(),
              },
            ]
          : [
              {
                type: 'button',
                label: t('Settings'),
                onClick: () => handleMenuClick(locations.settings),
              },
              {
                type: 'button',
                label: t('Download user manual'),
                onClick: handleDownloadManual,
              },
            ]
      }
      header={
        <TopNav>
          <TopNavItem
            active={location.pathname.startsWith(locations.datasets)}
            label={t('Sync')}
            onClick={() => handleMenuClick(locations.datasets)}
          />
          <TopNavItem
            active={location.pathname.startsWith(
              locations.optimizationProcesses
            )}
            label={t('Optimization processes')}
            onClick={() => handleMenuClick(locations.optimizationProcesses)}
          />
        </TopNav>
      }
      content={children}
    />
  )
}
